/**
 * Configuration
 */
import {app} from '@config/common'
/**
 * Helpers
 */
import string from '@helpers/string'
import {http, validate} from '@helpers/common'

/**
 * Types and Interface
 */
import {HttpResponse, UserInterface} from '@interface/common'


type UserLogin = {
  email: string,
  password: string
}


/**
 * Default state
 */
export const status = {
  error: '',
  success: '',
  preload: false
}


/**
 * Check role for authorization
 */
export function isAuthorized(role:string) {
  return app.authorized.includes(role)
}


/**
 * Logout user
 */
export function logout(data = {}, store:any) {
  store.build(() => {
    const param = {
      data: {
        path: '/office'
      }
    }
    return http.post('/auth/logout', param)
  })
  .next(() => {
    store.remove([
      'user',
      'logged'
    ])
    store.navigation.redirect('/')
  })
}


/**
 * Login user
 * @param data
 */
export async function login(data: UserLogin, store:any) {
  store.build(() => {
    store.update({
      auth: {
        status: {preload: true}
      }
    })
    
    if(!data.email || !data.password) {
      throw Error('Please fill empty field.')
    }

    return http.post('/auth/login', {
      headers: {
        Authorization: `Basic ${string.encode(`${data.email}:${data.password}`)}`
      },
    })
  })
  /**
   * Throw error to unauthorized user
   */
  .next(({status, result}: HttpResponse) => {
    if(status === 200) {
      if(!isAuthorized(result.organization.role)) {
        throw Error('You are not authorized to use this portal.')
      }
      return result
    }
  })
  /**
   * Add to persistent storage and redirect
   */
  .next((user: UserInterface) => {
    store.persist({
      user,
      logged: true
    })
    store.navigation.redirect('/office/accounts')
  })
  /**
   * All thrown error
   */
  .catch((e:any) => {
    status.error = e.message
    status.preload = false

    if(e.status === 401) {
      status.error = 'Invalid Email Address/Password'
    }
    store.update({auth: {status}})
  })
}


/**
 * Request code to reset password 
 * @param data
 */
export function forgot(data:{email: string}, store:any) {

  store.build(() => {
    store.update({
      auth: {
        status: {preload: true}
      }
    })
    /**
     * Send request
     */
    return http.post('/auth/code', {
      data: {
        email: data.email,
        origin: location.origin
      },
    })
  })
  .next(({status, result}: HttpResponse) => {
    if(status === 200) {
      /**
       * Remove persisted data after 10 minutes
       */
      setTimeout(() => {
        store.remove('user')
        store.update({
          auth: {
            status: {
              success: false,
              error: 'Session expired'
            }
          }
        })
      }, app.resetTimeout)

      return result
    }
  })
  .deploy((data:any) => {
    if(!data.success) {
      return
    }
    store.persist({
      user: {
        code: data.code,
        uid: data.user.uid,
        email: data.user.email
      }
    })
    return {
      auth: {
        status: {
          error: false,
          preload: false,
          success: 'We have successfully sent you an email. Please check and follow the instruction.',
        }
      },
    }
  })
  /**
   * All thrown error
   */
  .catch((e:any) => {
    status.error = e.message

    if(e.status === 401) {
      status.error = 'Unauthorized request, Please use your authorized email address.'
    }
    store.update({auth: {status, preload: false}})
  })
}


/**
 * Reset password 
 * @param data
 */
export function reset(data:any, store:any) {

  store.build(() => {
    const validated = validate.isPasswordStrong(data.data.password)
    /**
     * If password don't have enough strength
     */
    if(typeof validated == 'string') {
      throw Error(validated)
    }
    /**
     * If password has enough strength
     */
    if(data.data.password !== data.confirmPassword) {
      throw Error('Password doesn\'t matched.')
    }
    /**
     * Send request
     */
    return http.post('/auth/reset', {data: data.data})
  })

  .next(({status, result}: HttpResponse) => {
    if(status === 200) {
      store.remove('user')
      return result
    }
  })

  .delay(100, (data:any) => {
    if(data.success) {
      store.navigation.redirect('/')
    }
  })
  /**
   * All thrown error
   */
  .catch((e:any) => {
    status.error = e.message

    if(e.status === 401) {
      status.error = 'Unable to change your password.'
    }
    store.update({auth: {status, preload: false}})
  })
}